import {
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
} from 'react';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  useNavigate,
  useParams,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { AppDispatch, RootState } from '../app/store';
import { useAgentDispatch, useAgentSelector } from './useAgentStore';
import { Thread } from '../interfaces/copilotChat';
import { Notify } from '../services/notificationService';
import {
  getCopilotThreadMessages,
  mapThreadMessages,
  setCopilotChatSessionId,
  setRevokeAPICall,
  threadRequestInProgress,
} from '../services/copilotThread.service';
import { AppRoutes } from '../constants/route';
import {
  ADMIN_AGENT_ASSISTANT_TYPE,
  FAILED_THREAD_ID,
} from '../constants/adminAgent';
import { setGroupedThreads, setThreads } from '../app/Slices/adminAgentSlice';
import { confirmAction } from '../utils/confirmActionModal';
import { getThreadGroups } from '../utils/groupMenuBarItems';
import { makeApiCall } from '../utils/makeApiCall';
import {
  setNewThreadId,
  setChatMessages,
  setResult,
} from '../app/Slices/adminAgentSlice';
import {
  deleteChangeRequests,
  fetchChangeRequests,
} from '../app/ActionTypes/changeRequestsActions';
import * as subscriptionService from '../services/subscription';
import { ChangeRequest } from '../interfaces/changeRequest';
import { isOwnChangeRequest } from '../services/changeRequestService';
import ThreadItem from '../components/AppSideMenu/ThreadItem';
import ChangeRequestItem from '../components/AppSideMenu/ChangeRequestItem';
import { setChangeRequest } from '../app/Slices/changeRequestSlice';
import { AxiosError } from 'axios';

const _isLocationPathOfIndividualThreadPage = (_path: string) => {
  return _path.startsWith(`${AppRoutes.adminAgent.path}/thread/`);
};

interface SidebarMenuContextType {
  isLoading: boolean;
  setStatesForNewChat: () => void;
  setStatesForExistingChat: (threadId: string) => Promise<void>;
  navigateToNewChat: () => void;
  navigateToNewChangeAgent: () => void;
  addNewChatToThreadList: (threadId: string) => Promise<void>;
  moveThreadToTop: () => Promise<void>;
  isThreadsLoading: boolean;
  editingThreadId: string | null;
  getCrMenuItems: (cr: ChangeRequest) => MenuProps['items'];
  renderThreadItem: (thread: Thread) => React.ReactNode;
  renderChangeRequestItem: (cr: ChangeRequest) => React.ReactNode;
  getSelectedItemKey: (pathName: string) => string;
  initializeAdminAgent: () => Promise<void>;
  initializeChangeRequests: () => Promise<void>;
  handleNavigation: (navigateFn: () => void) => Promise<void>;
}

const SidebarMenuContext = createContext<SidebarMenuContextType | undefined>(
  undefined
);

export const SidebarMenuProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const urlParams = useParams<{
    threadId: string;
    changeRequestRouteId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const agentDispatch = useAgentDispatch();
  const org = useSelector((state: RootState) => state.organization.org);
  const location = useLocation();
  const navigationType = useNavigationType();

  const [isUserReadonly, setIsUserReadonly] = useState(
    subscriptionService.isUserReadonly(org?.status)
  );
  const { subscriptionInfo } = useSelector(
    (state: RootState) => state.subscription
  );
  useEffect(() => {
    setIsUserReadonly(subscriptionService.isUserReadonly(org?.status));
  }, [org, subscriptionInfo]);

  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [isThreadsLoading, setIsThreadsLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { newThreadId } = useAgentSelector((state) => state.adminAgent);
  const [isThreadRequestInProgress, setIsThreadRequestInProgress] = useState(
    threadRequestInProgress
  );

  const [editingCrId, setEditingCrId] = useState<string | null>(null);

  const fetchThreads = useCallback(
    async (showLoading = true) => {
      if (showLoading) {
        setIsThreadsLoading(true);
      }
      try {
        const response = await makeApiCall({
          url: '/threads',
          method: 'GET',
          params: ADMIN_AGENT_ASSISTANT_TYPE,
        });
        const sortedThreads = [...response.data].sort(
          (a: Thread, b: Thread) => {
            return (
              new Date(b.updated_at).getTime() -
              new Date(a.updated_at).getTime()
            );
          }
        );
        const newGroupedThreads = getThreadGroups([...sortedThreads]);
        agentDispatch(setThreads(sortedThreads));
        agentDispatch(setGroupedThreads(newGroupedThreads));
      } catch (error) {
        Notify.error(
          (error as Error).message || 'Unable to fetch chat history'
        );
      } finally {
        setIsThreadsLoading(false);
      }
    },
    [agentDispatch]
  );

  const setStatesForNewChat = useCallback(() => {
    setRevokeAPICall(true);
    setCopilotChatSessionId('', 3);
    agentDispatch(setNewThreadId('new-chat'));
    agentDispatch(setChatMessages([]));
    agentDispatch(setResult(null));
  }, [agentDispatch]);

  const setStatesForExistingChat = useCallback(
    async (threadId: string) => {
      try {
        setIsLoading(true);
        agentDispatch(setNewThreadId(''));
        setRevokeAPICall(true);
        setCopilotChatSessionId(threadId, 4);
        const messages = await getCopilotThreadMessages(threadId, 'asc');
        const { chat } = mapThreadMessages(messages);
        agentDispatch(setChatMessages(chat));
        agentDispatch(setResult(null));
        setIsLoading(false);
      } catch (error) {
        if (error instanceof AxiosError) {
          if (
            error.response?.status === 404 &&
            error.response?.data?.message === 'Thread not found'
          ) {
            throw new Error('Chat not found');
          }
        }
        setIsLoading(false);
        throw new Error('Unable to load chat');
      }
    },
    [agentDispatch]
  );

  const navigateToNewChat = useCallback(() => {
    navigate(AppRoutes.adminAgent.path);
  }, [setStatesForNewChat, navigate]);

  const navigateToNewChangeAgent = useCallback(() => {
    navigate(`${AppRoutes.changeRequests.path}/new`, { replace: true });
  }, [navigate]);

  const initializeAdminAgent = useCallback(async () => {
    try {
      setIsThreadsLoading(true);
      await fetchThreads();
      if (urlParams.threadId) {
        scrollToMenuItem(urlParams.threadId);
      }
    } catch (error) {
      Notify.error('Failed to initialize data');
    } finally {
      setIsThreadsLoading(false);
    }
  }, []);

  const initializeChangeRequests = useCallback(async () => {
    await agentDispatch(fetchChangeRequests());
    if (urlParams.changeRequestRouteId) {
      scrollToMenuItem(urlParams.changeRequestRouteId);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const newPath = location.pathname;
      if (navigationType !== 'REPLACE') {
        if (newPath === AppRoutes.adminAgent.path) {
          setStatesForNewChat();
        } else if (_isLocationPathOfIndividualThreadPage(newPath)) {
          try {
            setIsLoading(true);
            if (urlParams.threadId !== FAILED_THREAD_ID) {
              await setStatesForExistingChat(urlParams.threadId || '');
            }
          } catch (error) {
            Notify.error((error as Error)?.message);
            navigateToNewChat();
          } finally {
            setIsLoading(false);
          }
        }
      }
    })();
  }, [
    location,
    navigationType,
    urlParams.threadId,
    setStatesForNewChat,
    setStatesForExistingChat,
  ]);

  const addNewChatToThreadList = useCallback(
    async (_threadId: string) => {
      await fetchThreads();
      agentDispatch(setNewThreadId(_threadId));
      navigate(`${AppRoutes.adminAgent.path}/thread/${_threadId}`, {
        replace: true,
      });
    },
    [fetchThreads, agentDispatch, navigate]
  );

  const scrollToMenuItem = useCallback((menuItemId: string) => {
    // Add a small delay to ensure the DOM is updated with the new app menu items
    setTimeout(() => {
      const threadElement = document.querySelector(
        `[data-app-menu-id="${menuItemId}"]`
      );
      if (threadElement) {
        threadElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 0);
  }, []);

  const moveThreadToTop = useCallback(async () => {
    await fetchThreads(false);
  }, [fetchThreads]);

  useEffect(() => {
    const handleThreadRequestInProgressChange = (event: CustomEvent) => {
      setIsThreadRequestInProgress(event.detail);
    };

    window.addEventListener(
      'threadRequestInProgressChanged',
      handleThreadRequestInProgressChange as EventListener
    );

    return () => {
      window.removeEventListener(
        'threadRequestInProgressChanged',
        handleThreadRequestInProgressChange as EventListener
      );
    };
  }, []);

  const handleStartRenamingThread = useCallback(
    (thread: Thread) => {
      setEditingThreadId(thread.id);
    },
    [setEditingThreadId]
  );

  const handleDeleteChangeRequest = async (changeRequestId: string) => {
    try {
      const isConfirmed = await confirmAction({
        title: `Are you sure you want to delete this Change Request?`,
        okText: 'Delete',
        okType: 'danger',
      });

      if (!isConfirmed) return;

      if (changeRequestId === urlParams.changeRequestRouteId) {
        navigateToNewChangeAgent();
      }
      dispatch(deleteChangeRequests({ ids: [changeRequestId] }));
      Notify.success('Change Request deleted successfully');
    } catch (error) {
      Notify.error(
        (error as Error).message || 'Failed to delete change request'
      );
    }
  };

  const handleDeleteThread = useCallback(
    async (deleteThread: Thread) => {
      const deleteThreadId = deleteThread.id;
      try {
        const isConfirmed = await confirmAction({
          title: 'Are you sure you want to delete this Chat?',
          okText: 'Delete',
          okType: 'danger',
        });
        if (!isConfirmed) return;
        setIsThreadsLoading(true);
        await makeApiCall({
          url: '/threads/batch-delete',
          method: 'POST',
          data: {
            ids: [deleteThreadId],
          },
        });
        await fetchThreads(); // Refresh the thread list
        if (deleteThreadId === urlParams.threadId) {
          navigateToNewChat();
        }
        Notify.success('Chat deleted successfully');
      } catch (error) {
        Notify.error((error as Error).message || 'Failed to delete chat');
      } finally {
        setIsThreadsLoading(false);
      }
    },
    [urlParams.threadId, navigateToNewChat]
  );

  const getThreadMenuItems = useCallback(
    (thread: Thread): MenuProps['items'] => [
      {
        key: 'rename',
        icon: <EditOutlined />,
        label: 'Rename',
        disabled: isUserReadonly,
        onClick: (e) => {
          e.domEvent.stopPropagation();
          handleStartRenamingThread(thread);
        },
      },
      {
        key: 'delete',
        icon: <DeleteOutlined style={{ color: 'var(--red-5)' }} />,
        label: <span style={{ color: 'var(--red-5)' }}>Delete</span>,
        onClick: async (e) => {
          e.domEvent.stopPropagation();
          handleDeleteThread(thread);
        },
      },
    ],
    [isUserReadonly, handleStartRenamingThread, handleDeleteThread]
  );

  const handleSubmitNewThreadName = useCallback(
    async (thread: Thread, newTitle: string) => {
      try {
        if (newTitle && newTitle !== thread.title) {
          await makeApiCall({
            url: `/threads/${thread.id}`,
            method: 'PATCH',
            data: { title: newTitle },
          });
          fetchThreads(); // Refresh the thread list
        }
      } catch (error) {
        Notify.error((error as Error).message || 'Failed to rename chat');
      } finally {
        setEditingThreadId(null);
        setIsThreadsLoading(false);
      }
    },
    []
  );

  const renderThreadItem = (thread: Thread) => {
    return (
      <ThreadItem
        thread={thread}
        getActions={getThreadMenuItems}
        isCurrentThread={thread.id === urlParams.threadId}
        isProcessing={isThreadRequestInProgress}
        isEditingTitle={editingThreadId === thread.id}
        onTitleChange={handleSubmitNewThreadName}
        onAbortTitleChange={() => {
          setEditingThreadId(null);
        }}
      />
    );
  };

  const handleStartRenamingCr = useCallback(
    (cr: ChangeRequest) => {
      setEditingCrId(cr.id);
    },
    [setEditingCrId]
  );

  const handleSubmitNewChangeRequestName = useCallback(
    async (targetCr: ChangeRequest, newName: string) => {
      try {
        if (newName && newName !== targetCr.name) {
          await makeApiCall({
            url: `/cr/${targetCr.id}`,
            method: 'PATCH',
            data: [{ op: 'replace', path: '/name', value: newName }],
            headers: {
              'Content-Type': 'application/json-patch+json',
            },
          });
          await agentDispatch(fetchChangeRequests());
          agentDispatch(setChangeRequest({ id: targetCr.id, name: newName }));
        }
      } catch (error) {
        Notify.error((error as Error).message || 'Failed to rename chat');
      } finally {
        setEditingCrId(null);
      }
    },
    []
  );

  const renderChangeRequestItem = (cr: ChangeRequest) => {
    return (
      <ChangeRequestItem
        changeRequest={cr}
        org={org}
        getActions={getCrMenuItems}
        isEditingTitle={editingCrId === cr.id}
        onTitleChange={handleSubmitNewChangeRequestName}
        onAbortTitleChange={() => {
          setEditingCrId(null);
        }}
      />
    );
  };

  const getCrMenuItems = useCallback(
    (cr: ChangeRequest): MenuProps['items'] => {
      const isCrReadOnly = !cr || !isOwnChangeRequest(org, cr);
      const disabledDeletion = isCrReadOnly || isUserReadonly;
      const color = disabledDeletion ? 'var(--disabled-text)' : 'var(--red-5)';
      return [
        {
          key: 'rename',
          icon: <EditOutlined />,
          label: 'Rename',
          disabled: isCrReadOnly || isUserReadonly,
          onClick: (e) => {
            e.domEvent.stopPropagation();
            handleStartRenamingCr(cr);
          },
        },
        {
          key: 'delete',
          icon: <DeleteOutlined style={{ color }} />,
          label: <span style={{ color }}>Delete</span>,
          disabled: isCrReadOnly || isUserReadonly,
          onClick: async (e) => {
            e.domEvent.stopPropagation();
            handleDeleteChangeRequest(cr.id);
          },
        },
      ];
    },
    [isUserReadonly, handleDeleteChangeRequest]
  );

  const getSelectedItemKey = (pathName: string): string => {
    if (pathName.includes(AppRoutes.changeRequests.path)) {
      if (pathName === AppRoutes.changeRequests.path) {
        return 'all-change-requests';
      }
      if (pathName === `${AppRoutes.changeRequests.path}/new`) {
        return 'new-change-request';
      }
      if (urlParams.changeRequestRouteId) {
        return urlParams.changeRequestRouteId;
      }
    }
    if (pathName.includes(AppRoutes.adminAgent.path)) {
      if (pathName === AppRoutes.adminAgent.path) {
        return 'new-chat';
      }
      if (
        pathName ===
        `${AppRoutes.adminAgent.path}${AppRoutes.promptLibrary.path}`
      ) {
        return 'prompt-library';
      }
      return newThreadId || urlParams.threadId || 'new-chat';
    }
    return '';
  };

  const handleNavigation = async (navigateFn: () => void) => {
    if (threadRequestInProgress) {
      // TODO: handle confirmation modal
      // const isConfirmed = window.confirm(
      //   'Are you sure you want to leave this page? Action will be cancelled.'
      // );
      // if (!isConfirmed) return;
    }
    navigateFn();
  };

  const contextValue = {
    isLoading,
    setStatesForNewChat,
    setStatesForExistingChat,
    navigateToNewChat,
    navigateToNewChangeAgent,
    addNewChatToThreadList,
    moveThreadToTop,
    isThreadsLoading,
    editingThreadId,
    getCrMenuItems,
    renderThreadItem,
    renderChangeRequestItem,
    getSelectedItemKey,
    initializeAdminAgent,
    initializeChangeRequests,
    handleNavigation,
  };

  return (
    <SidebarMenuContext.Provider value={contextValue}>
      {children}
    </SidebarMenuContext.Provider>
  );
};

export const useSidebarMenuItem = () => {
  const context = useContext(SidebarMenuContext);
  if (!context) {
    throw new Error('useSidebarMenu must be used within a SidebarMenuProvider');
  }
  return context;
};
